import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useFamily } from '@/context/FamilyContext'
import { Input, Button } from '@/components/ui'

/**
 * Screen shown when a user is authenticated but hasn't completed family setup.
 * Creates the family record and then redirects to writer selection.
 */
export function FamilySetupRequired() {
  const { signOut, user } = useAuth()
  const { createFamily } = useFamily()
  const navigate = useNavigate()

  // Try to get name from user metadata first
  const defaultName = user?.user_metadata?.name || ''
  const [parentName, setParentName] = useState(defaultName)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleGetStarted = async () => {
    if (!parentName.trim()) {
      setError('Please enter your name')
      return
    }

    setIsLoading(true)
    setError(null)

    const success = await createFamily(parentName.trim())

    setIsLoading(false)

    if (success) {
      navigate('/select-writer')
    } else {
      setError('Failed to create family. Please try again.')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-kids-bg)] px-4">
      <div className="max-w-md w-full text-center">
        <div className="text-6xl mb-6">
          <span role="img" aria-label="family">👨‍👩‍👧‍👦</span>
        </div>
        <h1 className="text-2xl font-bold text-[var(--color-kids-text)] mb-4">
          Complete Your Setup
        </h1>
        <p className="text-[var(--color-kids-text-muted)] mb-8">
          Welcome! To get started with Inkwell Jr., we need to set up your family
          account and add your young writers.
        </p>

        <div className="space-y-6 text-left">
          <Input
            label="Your Name"
            placeholder="e.g., Sarah"
            value={parentName}
            onChange={(e) => setParentName(e.target.value)}
            error={error || undefined}
            autoFocus
          />

          <div className="space-y-4">
            <Button
              onClick={handleGetStarted}
              isLoading={isLoading}
              className="w-full"
              size="lg"
            >
              Get Started
            </Button>
            <button
              onClick={() => signOut()}
              className="block w-full px-6 py-3 border-2 border-[var(--color-kids-border)] text-[var(--color-kids-text)] rounded-xl font-semibold hover:bg-[var(--color-kids-surface)] transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
